import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ChevronDown, ChevronRight, X, ImagePlus, Eye } from "lucide-react";
import {
  fetchAdminAnnouncements,
  createAdminAnnouncement,
  deleteAdminAnnouncement,
  getAnnouncementImageUploadUrl,
  type AdminAnnouncement,
} from "../../api/announcements";
import { fetchAdminUsers, type AdminUserSummary } from "../../api/admin";
import { AnnouncementModal } from "../../components/AnnouncementOverlay";
import { Button } from "../../components/Button";
import { AtlasPanel } from "../../components/AtlasPanel";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const ANNOUNCEMENTS_KEY = ["admin/announcements"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 36,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};

// ─── Preview ──────────────────────────────────────────────────────────────────
// Renders the exact user-facing modal (AnnouncementModal) over the admin page.
// Dismiss just closes the preview; the CTA opens its target in a new tab so
// the admin can check the link without losing the page (or a half-written
// draft) underneath.

function AnnouncementPreview({
  item,
  onClose,
}: {
  item: Parameters<typeof AnnouncementModal>[0]["item"];
  onClose: () => void;
}) {
  return (
    <AnnouncementModal
      item={item}
      remaining={0}
      onDismiss={onClose}
      onCta={(url) => window.open(url, "_blank", "noopener,noreferrer")}
    />
  );
}

// ─── User picker (typeahead over /admin/users) ────────────────────────────────

type PickedUser = { id: string; email: string; name: string | null };

function UserPicker({ selected, onChange }: {
  selected: PickedUser[];
  onChange: (users: PickedUser[]) => void;
}) {
  const [search, setSearch] = React.useState("");

  const resultsQuery = useQuery({
    queryKey: ["admin/users", "picker", search],
    queryFn: () => fetchAdminUsers({ search, limit: 8 }),
    enabled: search.trim().length >= 2,
  });

  const selectedIds = new Set(selected.map((u) => u.id));
  const results = (resultsQuery.data?.users ?? []).filter(
    (u: AdminUserSummary) => !selectedIds.has(u.id),
  );

  return (
    <div>
      <label style={fieldLabelStyle}>Recipients</label>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map((u) => (
            <span
              key={u.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: 600,
                color: "var(--text-primary)",
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-pill)",
                padding: "3px 6px 3px 10px",
              }}
            >
              {u.email}
              <button
                onClick={() => onChange(selected.filter((s) => s.id !== u.id))}
                title="Remove"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, display: "flex" }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={inputStyle}
      />
      {search.trim().length >= 2 && (
        <div
          style={{
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            marginTop: 6,
            maxHeight: 180,
            overflowY: "auto",
          }}
        >
          {resultsQuery.isLoading ? (
            <div style={{ padding: 10, fontSize: 12, color: "var(--text-muted)" }}>Searching…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: "var(--text-muted)" }}>No matches</div>
          ) : (
            results.map((u: AdminUserSummary) => (
              <button
                key={u.id}
                onClick={() => {
                  onChange([...selected, { id: u.id, email: u.email, name: u.name }]);
                  setSearch("");
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  borderBottom: "1px solid var(--border-subtle)",
                  cursor: "pointer",
                  padding: "8px 10px",
                }}
              >
                <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{u.email}</span>
                {u.name && (
                  <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 8 }}>{u.name}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────────

function CreateAnnouncementDialog({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [audience, setAudience] = React.useState<"all" | "selected" | "new_users">("all");
  const [recipients, setRecipients] = React.useState<PickedUser[]>([]);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [imageUploading, setImageUploading] = React.useState(false);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const [ctaLabel, setCtaLabel] = React.useState("");
  const [ctaUrl, setCtaUrl] = React.useState("");
  const [showPreview, setShowPreview] = React.useState(false);
  const imageInputRef = React.useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File) => {
    setImageError(null);
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image must be 5 MB or smaller");
      return;
    }
    setImageUploading(true);
    try {
      const { uploadUrl, publicUrl } = await getAnnouncementImageUploadUrl(file.type);
      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!res.ok) throw new Error("Upload failed");
      setImageUrl(publicUrl);
    } catch (err) {
      setImageError(errorMessage(err, "Upload failed"));
    } finally {
      setImageUploading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: () =>
      createAdminAnnouncement({
        title: title.trim(),
        body: body.trim(),
        audience,
        userIds: audience === "selected" ? recipients.map((u) => u.id) : undefined,
        imageUrl: imageUrl ?? undefined,
        ctaLabel: ctaLabel.trim() || undefined,
        ctaUrl: ctaUrl.trim() || undefined,
      }),
    onSuccess: onCreated,
  });

  const ctaValid =
    (ctaLabel.trim().length === 0) === (ctaUrl.trim().length === 0) &&
    (ctaUrl.trim().length === 0 ||
      ctaUrl.trim().startsWith("https://") ||
      (ctaUrl.trim().startsWith("/") && !ctaUrl.trim().startsWith("//")));

  const valid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (audience !== "selected" || recipients.length > 0) &&
    ctaValid &&
    !imageUploading;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          width: 460,
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 20 }}>
          New announcement
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={fieldLabelStyle}>Title</label>
            <input
              type="text"
              placeholder="e.g. Atlas is out of beta"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={fieldLabelStyle}>Message</label>
            <textarea
              placeholder="What do you want to tell them?"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              style={{ ...inputStyle, height: "auto", padding: 10, resize: "vertical", lineHeight: 1.5, fontFamily: "inherit" }}
            />
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, marginBottom: 0 }}>
              Supports **bold**, *italic*, [links](https://…) and "- " bullet lists.
              Blank line = new paragraph.
            </p>
          </div>
          <div>
            <label style={fieldLabelStyle}>Image (optional)</label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadImage(file);
                e.target.value = "";
              }}
            />
            {imageUrl ? (
              <div style={{ position: "relative" }}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: "100%",
                    maxHeight: 160,
                    objectFit: "cover",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-subtle)",
                    display: "block",
                  }}
                />
                <button
                  onClick={() => setImageUrl(null)}
                  title="Remove image"
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "var(--radius-pill)",
                    background: "var(--scrim)",
                    border: "1px solid var(--border-strong)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<ImagePlus size={13} />}
                disabled={imageUploading}
                onClick={() => imageInputRef.current?.click()}
              >
                {imageUploading ? "Uploading…" : "Add image"}
              </Button>
            )}
            {imageError && (
              <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 4, marginBottom: 0 }}>
                {imageError}
              </p>
            )}
          </div>
          <div>
            <label style={fieldLabelStyle}>Button (optional)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="Label, e.g. See what's new"
                value={ctaLabel}
                maxLength={40}
                onChange={(e) => setCtaLabel(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                type="text"
                placeholder="https://… or /store"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                style={{ ...inputStyle, flex: 1.4 }}
              />
            </div>
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, marginBottom: 0 }}>
              Shown next to the dismiss button. Needs both a label and a link -
              https URL or an app path like /connected-sources.
            </p>
          </div>
          <div>
            <label style={fieldLabelStyle}>Audience</label>
            <div style={{ display: "flex", gap: 8 }}>
              {([
                { value: "all", label: "All users" },
                { value: "selected", label: "Specific users" },
                { value: "new_users", label: "New sign-ups" },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setAudience(opt.value)}
                  style={{
                    flex: 1,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    borderRadius: "var(--radius-md)",
                    border: `1px solid ${audience === opt.value ? "var(--border-gold)" : "var(--border-default)"}`,
                    background: audience === opt.value ? "var(--accent-soft)" : "none",
                    color: audience === opt.value ? "var(--text-accent)" : "var(--text-secondary)",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {audience === "all" && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, marginBottom: 0 }}>
                Shows to every current user. Accounts created after publishing won't see it.
              </p>
            )}
            {audience === "new_users" && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, marginBottom: 0 }}>
                A standing notice: shows only to accounts created after publishing, and keeps
                showing to each new sign-up until you delete it.
              </p>
            )}
          </div>
          {audience === "selected" && (
            <UserPicker selected={recipients} onChange={setRecipients} />
          )}
        </div>

        {mutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
            {errorMessage(mutation.error, "Failed to publish")}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 20 }}>
          <Button
            variant="secondary"
            leftIcon={<Eye size={14} />}
            disabled={title.trim().length === 0 || body.trim().length === 0 || imageUploading}
            onClick={() => setShowPreview(true)}
          >
            Preview
          </Button>
          <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={mutation.isPending || !valid} onClick={() => mutation.mutate()}>
              {mutation.isPending ? "Publishing…" : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      {showPreview && (
        <AnnouncementPreview
          item={{
            title: title.trim(),
            body: body.trim(),
            imageUrl: imageUrl,
            // Preview what would actually publish: the CTA only ships when
            // both halves are filled in.
            ctaLabel: ctaLabel.trim() && ctaUrl.trim() ? ctaLabel.trim() : null,
            ctaUrl: ctaLabel.trim() && ctaUrl.trim() ? ctaUrl.trim() : null,
            createdAt: new Date().toISOString(),
          }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

// ─── Announcement row ─────────────────────────────────────────────────────────

function AnnouncementRow({ item }: { item: AdminAnnouncement }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminAnnouncement(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
    },
  });

  const reach =
    item.audience === "all"
      ? "All users"
      : item.audience === "new_users"
        ? "New sign-ups"
        : `${item.recipientCount} user${item.recipientCount === 1 ? "" : "s"}`;

  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "16px minmax(0,2fr) 110px 110px 100px auto",
          alignItems: "center",
          gap: 12,
          padding: "12px 0",
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Hide details" : "Show details"}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, display: "flex" }}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.title}
          </div>
          {item.createdByEmail && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
              by {item.createdByEmail}
            </div>
          )}
        </div>

        <span style={{ fontSize: 12, fontWeight: 600, color: item.audience === "selected" ? "var(--text-secondary)" : "var(--text-accent)" }}>
          {reach}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {item.dismissedCount} dismissed
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {new Date(item.createdAt).toLocaleDateString()}
        </span>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Eye size={12} />}
            onClick={() => setPreviewing(true)}
          >
            Preview
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 size={12} />}
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (window.confirm(`Delete "${item.title}"? It disappears for everyone, including users' announcement archives.`)) {
                deleteMutation.mutate();
              }
            }}
          >
            Delete
          </Button>
        </div>
      </div>

      {deleteMutation.isError && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", paddingBottom: 10 }}>
          {errorMessage(deleteMutation.error, "Delete failed")}
        </p>
      )}

      {expanded && (
        <div style={{ padding: "0 0 14px 28px" }}>
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt=""
              style={{
                maxWidth: 280,
                width: "100%",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                marginBottom: 10,
                display: "block",
              }}
            />
          )}
          <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text-secondary)", whiteSpace: "pre-wrap", margin: 0 }}>
            {item.body}
          </p>
          {item.ctaLabel && item.ctaUrl && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 0" }}>
              Button: <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>{item.ctaLabel}</span> → {item.ctaUrl}
            </p>
          )}
          {item.audience === "selected" && item.recipients.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Recipients
              </span>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
                {item.recipients.map((r) => r.email).join(", ")}
              </div>
            </div>
          )}
        </div>
      )}

      {previewing && (
        <AnnouncementPreview item={item} onClose={() => setPreviewing(false)} />
      )}
    </div>
  );
}

// ─── AdminAnnouncementsPage ───────────────────────────────────────────────────

export default function AdminAnnouncementsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = React.useState(false);

  const listQuery = useQuery({
    queryKey: ANNOUNCEMENTS_KEY,
    queryFn: fetchAdminAnnouncements,
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const items = listQuery.data?.announcements ?? [];

  return (
    <AdminShell
      title="Announcements"
      action={
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => setShowCreate(true)}
        >
          New announcement
        </Button>
      }
    >
      <AtlasPanel title={`Published (${items.length})`}>
        {listQuery.isLoading ? (
          <div style={{ padding: 16, color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: "12px 0", color: "var(--text-muted)", fontSize: 13 }}>
            No announcements yet. Publish one to show a dismissible popup to users
            the next time they open Atlas.
          </div>
        ) : (
          <div>
            {items.map((item) => (
              <AnnouncementRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </AtlasPanel>

      {showCreate && (
        <CreateAnnouncementDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_KEY });
            void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
          }}
        />
      )}
    </AdminShell>
  );
}
